import { EventDispatcher } from '@seddi/three';
import { filterMeshes } from '@seddi/ui-three-viewer';
import { copy as copyBounds, create as createBounds, } from '@/core/orientedSphere';
import { create as createLookAt } from '@/viewer/cameraLookAt';
import { computeBounds as computeBoundsFromGeometry } from '@/viewer/garmentGeometryRegion';
import { computeBounds as computeBoundsFromMesh } from '@/viewer/meshRegion';
import { animateLookAt, computeLookAtFromBounds } from '@/viewer/perspectiveCamera';
export class ViewerFrameControls extends EventDispatcher {
    constructor(camera, element) {
        super();
        this.enabled = true;
        this.moveSpeed = 0.05;
        this.rotateSpeed = 0.18;
        /**
         * Move camera to frame.
         */
        this.moveToTarget = (() => {
            const lookAt = createLookAt();
            return () => {
                const { camera, frame } = this;
                computeLookAtFromBounds(camera, frame, lookAt);
                // Immediate transition.
                const { origin, target } = lookAt;
                camera.position.copy(origin);
                camera.lookAt(target.x, target.y, target.z);
                // Dispatch transition event.
                this.dispatchEvent(endEvent);
            };
        })();
        /**
         * Update camera to frame.
         */
        this.update = (() => {
            const lookAt = createLookAt();
            return () => {
                if (!this.enabled)
                    return;
                const { camera, frame } = this;
                computeLookAtFromBounds(camera, frame, lookAt);
                // Animated transition.
                const isDone = animateLookAt(camera, lookAt, moveSpeed, rotateSpeed);
                // Dispatch transition event.
                if (isDone) {
                    this.dispatchEvent(endEvent);
                }
            };
        })();
        this.camera = camera;
        this.element = element;
        this.frame_ = createBounds();
        this.regions = [];
        this.regionsBounds = new Map();
        this.sceneBounds = createBounds();
        this.models = new Map();
    }
    get frame() {
        return this.frame_;
    }
    set frame(value) {
        copyBounds(this.frame_, value);
    }
    get target() {
        return this.frame_.center;
    }
    dispose() {
        // empty
    }
    updateRegions(regions) {
        this.regions = regions;
        this.regionsBounds.clear();
        this.buildGarmentGeometry();
        this.buildModels();
    }
    updateGarmentGeometry(geometry) {
        this.geometry = geometry;
        this.buildGarmentGeometry();
    }
    updateModel(model) {
        var _a, _b;
        const meshes = filterMeshes(model);
        // Overwrite previous items with same key name
        for (const mesh of meshes) {
            const name = ((_b = (_a = mesh.userData) === null || _a === void 0 ? void 0 : _a.name) !== null && _b !== void 0 ? _b : mesh.name);
            this.models.set(name, mesh);
        }
        this.buildModel(meshes);
    }
    /**
     * Update selection.
     * @param logos Logos.
     * @param selection Selection.
     */
    updateSelection(logos, selection) {
        this.frame = this.sceneBounds;
        if (!selection)
            return;
        const logo = logos.find(l => l.id === selection);
        if (!logo)
            return;
        const frame = this.regionsBounds.get(logo.region);
        if (!frame)
            return;
        this.frame = frame;
    }
    buildGarmentGeometry() {
        const { geometry, regions, regionsBounds } = this;
        if (!geometry)
            return;
        for (const region of regions) {
            const { entityId, id } = region;
            if (regionsBounds.has(id))
                continue;
            const piece = geometry.pieces.get(entityId);
            if (!piece)
                continue;
            const frame = computeBoundsFromGeometry(region, piece);
            regionsBounds.set(id, frame);
        }
    }
    /**
     * Build regions from model.
     * @param model - Model resource.
     */
    buildModel(meshes) {
        for (const mesh of meshes) {
            this.buildModelMesh(mesh);
        }
    }
    buildModelMesh(mesh) {
        var _a, _b;
        const { regions, regionsBounds } = this;
        const name = ((_b = (_a = mesh.userData) === null || _a === void 0 ? void 0 : _a.name) !== null && _b !== void 0 ? _b : mesh.name);
        const subset = regions.filter(r => r.pieceName === name);
        for (const region of subset) {
            if (regionsBounds.has(region.id))
                continue;
            const frame = computeBoundsFromMesh(region, mesh);
            regionsBounds.set(region.id, frame);
        }
    }
    buildModels() {
        for (const [, mesh] of this.models) {
            this.buildModelMesh(mesh);
        }
    }
}
const endEvent = { type: 'end' };
const moveSpeed = 0.05;
const rotateSpeed = 0.18;
